import React, { useEffect, useState } from "react";
import {
  Box, Typography, Grid, Chip, Button, CircularProgress, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, MenuItem, InputAdornment, IconButton, Select, Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StorefrontIcon from "@mui/icons-material/Storefront";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNotifier } from "../../../Core/Notifier";
import ProcurementPremiumHeader from "../Shared/PremiumHeader";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

const SupplierPayments: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<any>(null);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [filterSupplier, setFilterSupplier] = useState("");

  // Payment modal
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ supplier_id: "", amount: 0, payment_method: "bank_transfer", payment_date: new Date().toISOString().split("T")[0], reference_number: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [supplierPayables, setSupplierPayables] = useState<any[]>([]);
  const [selectedPOs, setSelectedPOs] = useState<Record<number, number>>({});

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [payRes, sumRes, supRes] = await Promise.all([
        fetch(`${API_URL}/supplier-payments/getAll`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ supplier_id: filterSupplier || undefined }) }),
        fetch(`${API_URL}/procurement/payables-summary`, { headers: getHeaders() }),
        fetch(`${API_URL}/vendor/getAll`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ searchText: "" }) }),
      ]);
      const [payJson, sumJson, supJson] = await Promise.all([payRes.json(), sumRes.json(), supRes.json()]);
      if (payJson.success) setPayments(payJson.data || []);
      if (sumJson.success) setSummary(sumJson.data);
      if (supJson.data) setSuppliers((supJson.data || []).filter((s: any) => !s.isDelete));
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [filterSupplier]);

  const loadPayables = async (supplierId: string) => {
    try {
      const res = await fetch(`${API_URL}/supplier/${supplierId}/payables`, { headers: getHeaders() });
      const json = await res.json();
      if (json.success) setSupplierPayables(json.data?.filter((p: any) => p.balance > 0) || []);
    } catch { setSupplierPayables([]); }
  };

  const handlePay = async () => {
    if (!payForm.supplier_id || payForm.amount <= 0) return;
    setSaving(true);
    try {
      const allocations = Object.entries(selectedPOs).filter(([_, amt]) => amt > 0).map(([poId, amt]) => ({ po_id: Number(poId), amount: amt }));
      const res = await fetch(`${API_URL}/supplier-payments`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ ...payForm, supplier_id: Number(payForm.supplier_id), po_allocations: allocations.length > 0 ? allocations : undefined }),
      });
      const json = await res.json();
      if (json.success) { showMessage("Payment recorded"); setPayOpen(false); setSelectedPOs({}); loadData(); }
      else showErrorMessage(json.message || "Failed");
    } catch { showErrorMessage("Error recording payment"); } finally { setSaving(false); }
  };

  return (
    <Box sx={{ bgcolor: "#F6F8FA", minHeight: "100vh", p: 3 }}>
      <ProcurementPremiumHeader
        title="Supplier Payments"
        subtitle="Track payments, allocations, and outstanding balances across every vendor"
        icon={<AttachMoneyIcon />}
        badge="LEDGER"
        onRefresh={loadData}
        actions={[{
          label: "Record Payment",
          icon: <AddIcon sx={{ fontSize: 16 }} />,
          variant: "primary",
          onClick: () => {
            setPayForm({ supplier_id: "", amount: 0, payment_method: "bank_transfer", payment_date: new Date().toISOString().split("T")[0], reference_number: "", notes: "" });
            setSupplierPayables([]); setSelectedPOs({}); setPayOpen(true);
          },
        }]}
        kpis={summary ? [
          { label: "Total Payable",    value: `$${(summary.total_payable || 0).toLocaleString()}`,    sub: "Open balance to suppliers", icon: <AttachMoneyIcon />,  tone: "red",     urgent: (summary.total_payable || 0) > 0 },
          { label: "Total Paid",       value: `$${(summary.total_paid || 0).toLocaleString()}`,       sub: "All-time settled",          icon: <TrendingUpIcon />,   tone: "emerald" },
          { label: "Paid This Month",  value: `$${(summary.paid_this_month || 0).toLocaleString()}`,  sub: "Current period",            icon: <ReceiptLongIcon />,  tone: "gold" },
          { label: "Total Invoiced",   value: `$${(summary.total_invoiced || 0).toLocaleString()}`,   sub: "All supplier invoices",     icon: <StorefrontIcon />,   tone: "cyan" },
        ] : []}
      />

      <Box>
        {/* KPI strip moved into header */}

        {/* Filter */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Select size="small" displayEmpty value={filterSupplier} onChange={e => setFilterSupplier(e.target.value)}
            sx={{ minWidth: 200, borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } }}>
            <MenuItem value="">All Suppliers</MenuItem>
            {suppliers.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.customerCompanyName || `${s.customerFirstName} ${s.customerLastName}`}</MenuItem>)}
          </Select>
        </Box>

        {/* Payments Table */}
        <Box sx={{ bgcolor: "#fff", borderRadius: "10px", border: "1px solid #E5E7EB", overflow: "hidden" }}>
          {loading ? <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress size={24} sx={{ color: "#0B1E2D" }} /></Box> : (
            <TableContainer>
              <Table size="small" sx={{
                "& .MuiTableHead-root .MuiTableRow-root": { backgroundColor: "#0B1E2D" },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  backgroundColor: "#0B1E2D",
                  backgroundImage: "none",
                  color: "#fff",
                  fontSize: 10.5,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  borderBottom: "none",
                  boxShadow: "none",
                  py: 1.5,
                },
              }}>
                <TableHead>
                  <TableRow>
                    {["Date", "Supplier", "Amount", "Method", "Reference", "Allocations", "Notes"].map(h => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id} sx={{ "& td": { borderBottom: "1px solid #F1F5F9" }, "&:hover td": { bgcolor: "#F6F8FA" }, transition: "background 0.15s" }}>
                      <TableCell sx={{ fontSize: 12.5, color: "#334155" }}>{new Date(p.payment_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={600} color="#0B1E2D" sx={{ cursor: "pointer", "&:hover": { color: "#800080" } }}
                          onClick={() => navigate(`/vendor-view/${p.supplier_id}`)}>
                          {p.supplier?.customerCompanyName || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell><Typography fontSize={14.5} fontWeight={700} color="#0B1E2D">${(p.amount || 0).toFixed(2)}</Typography></TableCell>
                      <TableCell>
                        <Chip label={p.payment_method?.replace(/_/g, " ")} size="small"
                          sx={{ height: 24, fontSize: 10.5, fontWeight: 600, bgcolor: "#EEF2F7", color: "#0B1E2D", textTransform: "capitalize", borderRadius: "6px", border: "1px solid #E5E7EB" }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#64748B", fontFamily: "monospace" }}>{p.reference_number || "—"}</TableCell>
                      <TableCell>
                        {p.allocations?.length > 0 ? (
                          <Chip label={`${p.allocations.length} PO${p.allocations.length === 1 ? "" : "s"}`} size="small" clickable
                            sx={{ height: 22, fontSize: 10.5, fontWeight: 600, bgcolor: "#F4E8F4", color: "#800080", borderRadius: "6px", border: "1px solid #E5C7E5" }} />
                        ) : <Typography fontSize={11} color="#94A3B8">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11.5, color: "#64748B", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.notes || "—"}</TableCell>
                    </TableRow>
                  ))}
                  {payments.length === 0 && <TableRow><TableCell colSpan={7} sx={{ textAlign: "center", py: 6, color: "#94A3B8" }}>No payments recorded</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>

      {/* Payment Dialog */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between" }}>
          <Typography fontSize={16} fontWeight={600} color="#111827">Record Payment</Typography>
          <IconButton size="small" onClick={() => setPayOpen(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography fontSize={12} fontWeight={500} color="#6B7280" mb={0.5}>Supplier</Typography>
            <Select fullWidth size="small" value={payForm.supplier_id} onChange={e => { setPayForm(f => ({ ...f, supplier_id: e.target.value })); loadPayables(e.target.value); }}
              sx={{ borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } }}>
              <MenuItem value="" disabled>Select supplier</MenuItem>
              {suppliers.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.customerCompanyName || `${s.customerFirstName} ${s.customerLastName}`}</MenuItem>)}
            </Select>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography fontSize={12} fontWeight={500} color="#6B7280" mb={0.5}>Amount</Typography>
              <TextField fullWidth size="small" type="number" value={payForm.amount || ""} onChange={e => setPayForm(f => ({ ...f, amount: Number(e.target.value) }))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
            </Grid>
            <Grid item xs={6}>
              <Typography fontSize={12} fontWeight={500} color="#6B7280" mb={0.5}>Date</Typography>
              <TextField fullWidth size="small" type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
            </Grid>
            <Grid item xs={6}>
              <Typography fontSize={12} fontWeight={500} color="#6B7280" mb={0.5}>Method</Typography>
              <Select fullWidth size="small" value={payForm.payment_method} onChange={e => setPayForm(f => ({ ...f, payment_method: e.target.value }))}
                sx={{ borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } }}>
                {["cash", "bank_transfer", "cheque", "e_transfer"].map(m => <MenuItem key={m} value={m} sx={{ textTransform: "capitalize" }}>{m.replace(/_/g, " ")}</MenuItem>)}
              </Select>
            </Grid>
            <Grid item xs={6}>
              <Typography fontSize={12} fontWeight={500} color="#6B7280" mb={0.5}>Reference #</Typography>
              <TextField fullWidth size="small" value={payForm.reference_number} onChange={e => setPayForm(f => ({ ...f, reference_number: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
            </Grid>
          </Grid>

          {/* Outstanding POs to allocate */}
          {supplierPayables.length > 0 && (
            <Box>
              <Typography fontSize={12} fontWeight={600} color="#374151" mb={1}>Allocate to Purchase Orders</Typography>
              <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, overflow: "hidden" }}>
                <Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
                    {["PO #", "Balance", "Allocate"].map(h => <TableCell key={h} sx={{ fontSize: 10, fontWeight: 500, color: "#6B7280" }}>{h}</TableCell>)}
                  </TableRow></TableHead>
                  <TableBody>
                    {supplierPayables.map((po: any) => (
                      <TableRow key={po.po_id}>
                        <TableCell sx={{ fontSize: 12 }}>{po.po_number}</TableCell>
                        <TableCell><Typography fontSize={12} fontWeight={600} color="#DC2626">${po.balance.toFixed(2)}</Typography></TableCell>
                        <TableCell>
                          <TextField size="small" type="number" value={selectedPOs[po.po_id] || ""} placeholder="0"
                            onChange={e => setSelectedPOs(prev => ({ ...prev, [po.po_id]: Math.min(Number(e.target.value), po.balance) }))}
                            inputProps={{ min: 0, max: po.balance, step: 0.01 }}
                            sx={{ width: 100, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 12, "& fieldset": { borderColor: "#E5E7EB" } } }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          <TextField size="small" label="Notes" multiline rows={2} value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
        </Box>
        <Box sx={{ px: 3, py: 2, bgcolor: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button variant="outlined" size="small" onClick={() => setPayOpen(false)}
            sx={{ borderRadius: 1.5, textTransform: "none", borderColor: "#D1D5DB", color: "#374151" }}>Cancel</Button>
          <Button variant="contained" disableElevation size="small" onClick={handlePay} disabled={saving || !payForm.supplier_id || payForm.amount <= 0}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 14 }} />}
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, bgcolor: "#059669", "&:hover": { bgcolor: "#047857" } }}>
            {saving ? "Saving..." : "Save Payment"}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SupplierPayments;
